import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "flowbite-react";
import { CustomSelect } from "../Select/CustomSelect";
import { getApiToken } from "../../data/api/token";
import {
  getVehicleDrivers,
  setVehicleDrivers,
  VehicleDriverAccessData,
} from "../../data/api/vehicles";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";

export function VehicleDriverAccess({
  workspaceId,
  vehicleId,
}: {
  workspaceId: string;
  vehicleId: string;
}) {
  const { t } = useTranslation("vehicles");
  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const canEdit = ["Owner", "Administrator"].includes(
    activeWorkspace?.role || "",
  );
  const [data, setData] = useState<VehicleDriverAccessData>();
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setError(false);
    getApiToken()
      .then((token) => getVehicleDrivers(token, workspaceId, vehicleId))
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setSelected(result.driverIds);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, vehicleId, reload]);
  const changed =
    data &&
    [...selected].sort().join(",") !== [...data.driverIds].sort().join(",");
  const save = async () => {
    if (!data) return;
    setBusy(true);
    setError(false);
    try {
      const result = await setVehicleDrivers(
        await getApiToken(),
        workspaceId,
        vehicleId,
        selected,
        data.driverIds,
      );
      setData(result);
      setSelected(result.driverIds);
      updateCacheKey();
      setReload((value) => value + 1);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4" aria-label={t("access.title")}>
      <div className="mb-2 text-sm font-medium">{t("access.title")}</div>
      {error && (
        <div role="alert" className="mb-3 text-sm text-red-600">
          {t("access.error")}{" "}
          <button
            type="button"
            disabled={busy}
            onClick={() => setReload((value) => value + 1)}
            className="underline"
          >
            {t("access.refresh")}
          </button>
        </div>
      )}
      {!data && !error && <p role="status">{t("access.loading")}</p>}
      {data && (
        <>
          <CustomSelect
            key={`${vehicleId}-${reload}-${data.driverIds.join(",")}`}
            type="checkbox"
            label={t("access.select")}
            disabled={!canEdit || busy}
            defaultValue={selected}
            items={data.drivers.map((driver) => ({key: driver.id, value: driver.id, label: driver.name}))}
            onSelectChanged={(value) => { if (Array.isArray(value)) setSelected(value); }}
          />
          {data.drivers.length === 0 && <p className="mt-2 text-sm text-muted">{t("access.empty")}</p>}
          {canEdit && changed && (
            <div className="mt-2">
              {data.drivers.some((driver) => driver.defaultVehicle && data.driverIds.includes(driver.id) && !selected.includes(driver.id)) && (
                <p className="mb-2 text-xs text-muted">{t("access.removal")}</p>
              )}
              <Button size="xs" type="button" disabled={busy} onClick={save}>
                {t(busy ? "access.saving" : "access.save")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
